<template>
  <div>
    <div class="page-header">
      <h1>🔔 提醒</h1>
      <p>管理定期提醒規則與收件匣</p>
    </div>

    <!-- 收件匣 -->
    <div class="card">
      <div class="card-title-row">
        <span class="card-title">📬 提醒收件匣</span>
        <button v-if="unreadCount > 0" class="btn btn-secondary btn-sm" @click="markAllRead">
          全部標為已讀
        </button>
      </div>
      <div v-if="inboxLoading" class="empty">載入中...</div>
      <div v-else-if="reminders.length === 0" class="empty">目前沒有提醒</div>
      <div v-else class="inbox-list">
        <div v-for="r in reminders" :key="r.id"
             :class="['inbox-item', { unread: !r.is_read }]">
          <div class="inbox-content">
            <span v-if="!r.is_read" class="dot"></span>
            <span class="inbox-msg">{{ r.message }}</span>
            <span class="inbox-date">{{ r.triggered_date }}</span>
          </div>
          <button v-if="!r.is_read" class="btn btn-secondary btn-sm" @click="markRead(r)">已讀</button>
        </div>
      </div>
    </div>

    <!-- 新增規則 -->
    <div class="card">
      <div class="card-title">➕ 新增提醒規則</div>
      <div class="form-row">
        <div class="form-group form-group-wide">
          <label>提醒內容</label>
          <input type="text" v-model="form.message" placeholder="提醒文字" />
        </div>
        <div class="form-group">
          <label>觸發條件</label>
          <select v-model="form.condType">
            <option value="dom">每月第幾日</option>
            <option value="dow">每週幾</option>
            <option value="mdom">特定月份 + 日</option>
            <option value="months">多月份 + 日</option>
          </select>
        </div>

        <div class="form-group" v-if="form.condType === 'dom'">
          <label>日期</label>
          <input type="number" v-model="form.day_of_month" min="1" max="31" placeholder="1~31" />
        </div>

        <div class="form-group" v-if="form.condType === 'dow'">
          <label>星期</label>
          <select v-model="form.day_of_week">
            <option value="0">週一</option><option value="1">週二</option>
            <option value="2">週三</option><option value="3">週四</option>
            <option value="4">週五</option><option value="5">週六</option>
            <option value="6">週日</option>
          </select>
        </div>

        <template v-if="form.condType === 'mdom'">
          <div class="form-group">
            <label>月份</label>
            <select v-model="form.month">
              <option v-for="m in 12" :key="m" :value="m">{{ m }} 月</option>
            </select>
          </div>
          <div class="form-group">
            <label>日期</label>
            <input type="number" v-model="form.day_of_month" min="1" max="31" />
          </div>
        </template>

        <template v-if="form.condType === 'months'">
          <div class="form-group">
            <label>月份（逗號分隔）</label>
            <input type="text" v-model="form.months" placeholder="例：1,3,5,7,9,11" />
          </div>
          <div class="form-group">
            <label>日期</label>
            <input type="number" v-model="form.day_of_month" min="1" max="31" />
          </div>
        </template>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" @click="addRule" :disabled="adding">
          {{ adding ? '新增中...' : '新增規則' }}
        </button>
        <span v-if="msg" class="msg-success">✓ {{ msg }}</span>
        <span v-if="errMsg" class="msg-error">✗ {{ errMsg }}</span>
      </div>
    </div>

    <!-- 規則列表 -->
    <div class="card">
      <div class="card-title">📋 現有規則</div>
      <div v-if="rulesLoading" class="empty">載入中...</div>
      <div v-else-if="rules.length === 0" class="empty">尚無提醒規則</div>
      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>提醒內容</th>
              <th>觸發條件</th>
              <th>上次觸發</th>
              <th>啟用</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rules" :key="r.id" :class="{ 'row-disabled': !r.enabled }">
              <td>{{ r.message }}</td>
              <td>{{ formatCond(r) }}</td>
              <td>{{ r.last_triggered_date || '-' }}</td>
              <td>
                <label class="toggle">
                  <input type="checkbox" :checked="r.enabled" @change="toggleRule(r)" />
                  <span class="toggle-slider"></span>
                </label>
              </td>
              <td class="td-actions">
                <button class="btn btn-edit btn-sm" @click="openEdit(r)">編輯</button>
                <button class="btn btn-danger btn-sm" @click="deleteRule(r)">刪除</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 編輯 Modal -->
    <div v-if="editModal.show" class="modal-backdrop" @click.self="closeEdit">
      <div class="modal">
        <div class="modal-header">
          <span>✏️ 編輯提醒規則</span>
          <button class="modal-close" @click="closeEdit">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group form-group-wide">
              <label>提醒內容</label>
              <input type="text" v-model="editModal.message" />
            </div>
            <div class="form-group">
              <label>觸發條件</label>
              <select v-model="editModal.condType">
                <option value="dom">每月第幾日</option>
                <option value="dow">每週幾</option>
                <option value="mdom">特定月份 + 日</option>
                <option value="months">多月份 + 日</option>
              </select>
            </div>

            <div class="form-group" v-if="editModal.condType === 'dom'">
              <label>日期</label>
              <input type="number" v-model="editModal.day_of_month" min="1" max="31" />
            </div>

            <div class="form-group" v-if="editModal.condType === 'dow'">
              <label>星期</label>
              <select v-model="editModal.day_of_week">
                <option value="0">週一</option><option value="1">週二</option>
                <option value="2">週三</option><option value="3">週四</option>
                <option value="4">週五</option><option value="5">週六</option>
                <option value="6">週日</option>
              </select>
            </div>

            <template v-if="editModal.condType === 'mdom'">
              <div class="form-group">
                <label>月份</label>
                <select v-model="editModal.month">
                  <option v-for="m in 12" :key="m" :value="m">{{ m }} 月</option>
                </select>
              </div>
              <div class="form-group">
                <label>日期</label>
                <input type="number" v-model="editModal.day_of_month" min="1" max="31" />
              </div>
            </template>

            <template v-if="editModal.condType === 'months'">
              <div class="form-group">
                <label>月份（逗號分隔）</label>
                <input type="text" v-model="editModal.months" placeholder="例：1,3,5,7,9,11" />
              </div>
              <div class="form-group">
                <label>日期</label>
                <input type="number" v-model="editModal.day_of_month" min="1" max="31" />
              </div>
            </template>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" @click="saveEdit" :disabled="editModal.saving">
            {{ editModal.saving ? '儲存中...' : '儲存' }}
          </button>
          <button class="btn btn-secondary" @click="closeEdit">取消</button>
          <span v-if="editModal.msg" class="msg-success">✓ {{ editModal.msg }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
axios.defaults.baseURL = 'https://mb.kman.idv.tw:9579/'

const DOW = ['週一', '週二', '週三', '週四', '週五', '週六', '週日']

export default {
  data() {
    return {
      rules: [], reminders: [],
      rulesLoading: true, inboxLoading: true,
      adding: false, msg: '', errMsg: '',
      form: {
        message: '', condType: 'dom',
        day_of_month: null, day_of_week: '0', month: 1, months: ''
      },
      editModal: {
        show: false, saving: false, msg: '',
        id: null, message: '', condType: 'dom',
        day_of_month: null, day_of_week: '0', month: 1, months: '',
        enabled: true
      }
    }
  },
  computed: {
    unreadCount() { return this.reminders.filter(r => !r.is_read).length }
  },
  async mounted() {
    await Promise.all([this.loadRules(), this.loadReminders()])
  },
  methods: {
    authHeader() {
      return { headers: { Authorization: 'Bearer ' + this.$store.getters.setToken } }
    },
    async loadRules() {
      this.rulesLoading = true
      const r = await axios.get('reminder_rules', this.authHeader())
      this.rules = (r.data.rules || []).map(x => ({
        id: x[0], message: x[1], day_of_month: x[2], day_of_week: x[3],
        month: x[4], months: x[5], enabled: x[6], last_triggered_date: x[7]
      }))
      this.rulesLoading = false
    },
    async loadReminders() {
      this.inboxLoading = true
      const r = await axios.get('reminders', this.authHeader())
      this.reminders = (r.data.reminders || []).map(x => ({
        id: x[0], message: x[1], triggered_date: x[2], is_read: x[3]
      }))
      this.inboxLoading = false
    },
    formatCond(r) {
      if (r.day_of_week !== null && r.day_of_week !== undefined) return '每' + DOW[r.day_of_week]
      if (r.months) return r.months + '月 ' + r.day_of_month + '日'
      if (r.month) return r.month + '月 ' + r.day_of_month + '日'
      if (r.day_of_month) return '每月 ' + r.day_of_month + '日'
      return '-'
    },
    buildPayload(f, token) {
      return {
        message: f.message,
        day_of_month: f.condType !== 'dow' ? parseInt(f.day_of_month) || null : null,
        day_of_week: f.condType === 'dow' ? parseInt(f.day_of_week) : null,
        month: f.condType === 'mdom' ? parseInt(f.month) : null,
        months: f.condType === 'months' ? f.months : null,
        enabled: true, token
      }
    },
    async addRule() {
      if (!this.form.message) { this.errMsg = '請輸入提醒內容'; return }
      this.adding = true; this.errMsg = ''
      try {
        await axios.post('add_reminder_rule', this.buildPayload(this.form, this.$store.getters.setToken))
        this.msg = '新增成功'
        this.form.message = ''
        setTimeout(() => this.msg = '', 3000)
        await this.loadRules()
      } catch { this.errMsg = '新增失敗' }
      this.adding = false
    },
    async toggleRule(r) {
      try {
        await axios.post('update_reminder_rule', {
          id: r.id, message: r.message,
          day_of_month: r.day_of_month, day_of_week: r.day_of_week,
          month: r.month, months: r.months,
          enabled: !r.enabled, token: this.$store.getters.setToken
        })
        r.enabled = !r.enabled
      } catch { alert('更新失敗') }
    },
    async deleteRule(r) {
      if (!confirm(`確定刪除「${r.message}」？`)) return
      try {
        await axios.post('delete_reminder_rule', { id: r.id, token: this.$store.getters.setToken })
        this.rules = this.rules.filter(x => x.id !== r.id)
      } catch { alert('刪除失敗') }
    },
    openEdit(r) {
      const condType = r.day_of_week !== null && r.day_of_week !== undefined ? 'dow'
        : r.months ? 'months' : r.month ? 'mdom' : 'dom'
      Object.assign(this.editModal, {
        show: true, saving: false, msg: '',
        id: r.id, message: r.message, condType,
        day_of_month: r.day_of_month,
        day_of_week: r.day_of_week !== null && r.day_of_week !== undefined ? String(r.day_of_week) : '0',
        month: r.month || 1,
        months: r.months || '',
        enabled: r.enabled
      })
    },
    closeEdit() { this.editModal.show = false },
    async saveEdit() {
      const e = this.editModal
      e.saving = true
      try {
        await axios.post('update_reminder_rule', {
          id: e.id, message: e.message,
          day_of_month: e.condType !== 'dow' ? parseInt(e.day_of_month) || null : null,
          day_of_week: e.condType === 'dow' ? parseInt(e.day_of_week) : null,
          month: e.condType === 'mdom' ? parseInt(e.month) : null,
          months: e.condType === 'months' ? e.months : null,
          enabled: e.enabled, token: this.$store.getters.setToken
        })
        e.msg = '已儲存'
        setTimeout(() => { e.msg = ''; e.show = false }, 1200)
        await this.loadRules()
      } catch { alert('儲存失敗') }
      e.saving = false
    },
    async markRead(r) {
      try {
        await axios.post('mark_reminder_read', { id: r.id, token: this.$store.getters.setToken })
        r.is_read = true
      } catch { alert('操作失敗') }
    },
    async markAllRead() {
      try {
        await axios.post('mark_all_reminders_read', { id: 0, token: this.$store.getters.setToken })
        this.reminders.forEach(r => r.is_read = true)
      } catch { alert('操作失敗') }
    }
  }
}
</script>

<style scoped>
.page-header { margin-bottom: 24px; }
.page-header h1 { font-size: 24px; font-weight: 700; color: var(--text); }
.page-header p { color: var(--text-light); margin-top: 4px; font-size: 14px; }

.card {
  background: var(--card);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 20px;
  margin-bottom: 20px;
}
.card-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid var(--border); }
.card-title-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid var(--border); }
.card-title-row .card-title { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }

.inbox-list { display: flex; flex-direction: column; gap: 8px; }
.inbox-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px; border-radius: 8px;
  background: #f9fafb; border: 1px solid var(--border);
}
.inbox-item.unread { background: #eff6ff; border-color: #bfdbfe; }
.inbox-content { display: flex; align-items: center; gap: 8px; flex: 1; }
.dot { width: 8px; height: 8px; background: var(--primary); border-radius: 50%; flex-shrink: 0; }
.inbox-msg { font-size: 14px; color: var(--text); font-weight: 500; }
.inbox-date { font-size: 12px; color: var(--text-light); margin-left: auto; margin-right: 12px; }

.form-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
.form-group { display: flex; flex-direction: column; gap: 4px; min-width: 130px; }
.form-group-wide { min-width: 240px; flex: 1; }
.form-group label { font-size: 12px; font-weight: 500; color: var(--text-light); }
.form-group select,
.form-group input[type=text],
.form-group input[type=number] {
  padding: 8px 10px; border: 1px solid var(--border); border-radius: 6px;
  font-size: 14px; color: var(--text); background: #fff; outline: none; transition: border 0.15s;
}
.form-group select:focus, .form-group input:focus { border-color: var(--primary); }

.form-actions { display: flex; align-items: center; gap: 12px; margin-top: 4px; }
.msg-success { color: var(--success); font-size: 14px; }
.msg-error { color: var(--danger); font-size: 14px; }

.btn { padding: 8px 18px; border: none; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.15s; }
.btn-primary { background: var(--primary); color: #fff; }
.btn-primary:hover:not(:disabled) { background: var(--primary-hover); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: var(--text); border: 1px solid var(--border); }
.btn-secondary:hover { background: #e5e7eb; }
.btn-danger { background: #fef2f2; color: var(--danger); border: 1px solid #fecaca; }
.btn-danger:hover { background: #fee2e2; }
.btn-edit { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
.btn-edit:hover { background: #dbeafe; }
.btn-sm { padding: 4px 10px; font-size: 12px; }

.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.data-table th { text-align: left; padding: 10px 12px; font-size: 12px; font-weight: 600; color: var(--text-light); background: #f9fafb; border-bottom: 1px solid var(--border); }
.data-table td { padding: 10px 12px; border-bottom: 1px solid var(--border); color: var(--text); }
.data-table tr:last-child td { border-bottom: none; }
.data-table tr:hover td { background: #f9fafb; }
.row-disabled td { opacity: 0.5; }
.td-actions { display: flex; gap: 6px; }

.toggle { position: relative; display: inline-block; width: 36px; height: 20px; }
.toggle input { opacity: 0; width: 0; height: 0; }
.toggle-slider { position: absolute; inset: 0; background: #d1d5db; border-radius: 99px; cursor: pointer; transition: background 0.2s; }
.toggle-slider::before { content: ''; position: absolute; width: 14px; height: 14px; left: 3px; top: 3px; background: #fff; border-radius: 50%; transition: transform 0.2s; }
.toggle input:checked + .toggle-slider { background: var(--primary); }
.toggle input:checked + .toggle-slider::before { transform: translateX(16px); }

.modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 200; }
.modal { background: #fff; border-radius: var(--radius); box-shadow: 0 20px 60px rgba(0,0,0,0.2); width: 100%; max-width: 600px; max-height: 90vh; overflow-y: auto; margin: 16px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid var(--border); font-weight: 600; font-size: 16px; }
.modal-close { background: none; border: none; font-size: 16px; color: var(--text-light); cursor: pointer; padding: 4px 8px; border-radius: 4px; }
.modal-close:hover { background: #f3f4f6; }
.modal-body { padding: 20px; }
.modal-footer { padding: 16px 20px; border-top: 1px solid var(--border); display: flex; gap: 10px; align-items: center; }

.empty { text-align: center; color: var(--text-light); padding: 40px; }
</style>
